// src/pages/AutoSignOut.tsx
import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { apiGet, apiPatch } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type TimeoutOption = { id: number; label: string };

type Props = {
  mode?: "page" | "inlay";
  onClose?: () => void;
  onTokenUpdated?: (token: string) => void;
};

const DEFAULT_TIMEOUT = 120;

type ErrorOutcome =
  | { kind: "local"; code: "inactiveUser" | "staffProRequired" | "failed" }
  | { kind: "raw"; value: React.ReactNode };

function getErrorOutcome(e: any): ErrorOutcome {
  const detail = e?.response?.data?.detail;
  // apiPatch omits status; recognition is deliberately exact-detail-only.
  if (detail === "Inactive user.") return { kind: "local", code: "inactiveUser" };
  if (detail === "Staff access requires an active Pro subscription.") return { kind: "local", code: "staffProRequired" };
  return detail ? { kind: "raw", value: detail } : { kind: "local", code: "failed" };
}

export default function AutoSignOut({
  mode = "inlay",
  onClose,
  onTokenUpdated,
}: Props) {
  const { t, i18n } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const languageSelection = useRef<{
    input: HTMLInputElement; start: number | null; end: number | null;
    direction: "forward" | "backward" | "none" | null;
  } | null>(null);

  // Headless UI rewrites translated display values, moving the caret. Capture
  // only this focused input before a language render and restore it afterward.
  useEffect(() => {
    const captureSelection = () => {
      const input = rootRef.current?.querySelector<HTMLInputElement>('input[role="combobox"]');
      languageSelection.current = input && document.activeElement === input ? {
        input, start: input.selectionStart, end: input.selectionEnd,
        direction: input.selectionDirection,
      } : null;
    };
    i18n.on("languageChanged", captureSelection);
    return () => { i18n.off("languageChanged", captureSelection); };
  }, [i18n]);

  // The input's passive display-value effect runs before this parent's effect.
  // Restore once after that rewrite, without changing the input value or focus.
  useEffect(() => {
    const selection = languageSelection.current;
    languageSelection.current = null;
    if (selection && document.activeElement === selection.input) {
      const length = selection.input.value.length;
      selection.input.setSelectionRange(
        Math.min(selection.start ?? 0, length),
        Math.min(selection.end ?? 0, length),
        selection.direction ?? undefined,
      );
    }
  }, [t]);
  const [minutes, setMinutes] = useState<number>(DEFAULT_TIMEOUT);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(false);
  const [error, setError] = useState<ErrorOutcome | null>(null);
  const errorText = !error ? null : error.kind === "raw" ? error.value :
    error.code === "inactiveUser" ? t("accountInlays.errors.inactiveUser") :
    error.code === "staffProRequired" ? t("accountInlays.errors.staffProRequired") :
    t("autoSignOut.failed");

  // Load current timeout
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const st = await apiGet<{ minutes: number; override: number | null }>(
          "/auth/session-timeout"
        );
        if (cancelled) return;

        const m =
          Number(st?.override ?? st?.minutes ?? DEFAULT_TIMEOUT) ||
          DEFAULT_TIMEOUT;
        setMinutes(m);
      } catch {
        // ignore; keep default
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Include “current” option if backend returns something not in standard list
  const timeoutOptions = useMemo<TimeoutOption[]>(() => {
    const baseOptions: TimeoutOption[] = [
      { id: 30, label: t("autoSignOut.minutes30") },
      { id: 60, label: t("autoSignOut.hour1") },
      { id: 120, label: t("autoSignOut.hours2Default") },
      { id: 240, label: t("autoSignOut.hours4") },
      { id: 480, label: t("autoSignOut.hours8") },
      { id: 720, label: t("autoSignOut.hours12") },
    ];
    const has = baseOptions.some((o) => o.id === minutes);
    if (!has && Number.isFinite(minutes) && minutes > 0) {
      return [
        ...baseOptions,
        { id: minutes, label: t("autoSignOut.currentMinutes", { minutes }) },
      ].sort((a, b) => a.id - b.id);
    }
    return baseOptions;
  }, [minutes, t]);

  const selected = useMemo<TimeoutOption | null>(() => {
    return timeoutOptions.find((o) => o.id === minutes) || null;
  }, [timeoutOptions, minutes]);

  const handleSave = async () => {
    setSaving(true);
    setMsg(false);
    setError(null);

    try {
      const res = await apiPatch<{
        access_token: string;
        token_type: string;
        company_id: number;
      }>("/auth/session-timeout", { minutes });

      if (res?.access_token) {
        localStorage.setItem("access_token", res.access_token);
        onTokenUpdated?.(res.access_token);
      }

      setMsg(true);
      setTimeout(() => onClose?.(), 700);
    } catch (e: any) {
      setError(getErrorOutcome(e));
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <>
      <div className={`${A.label} mb-2`}>
        {t("autoSignOut.explanation")}
      </div>

      <AppleComboBox<TimeoutOption>
        options={timeoutOptions}
        value={selected}
        onChange={(opt) => setMinutes(opt ? Number(opt.id) : DEFAULT_TIMEOUT)}
        getOptionLabel={(opt) => (opt ? opt.label : "")}
        placeholder={t("autoSignOut.selectTimeout")}
        emptyText={t("sales.shared.noResults")}
        toggleOptionsLabel={t("sales.shared.toggleOptions")}
        preserveQueryOnDisplayChange
      />

      <div className="mt-4">
        <Button
          className="w-full justify-center"
          type="button"
          variant="primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? t("directory.common.savingDots") : t("directory.common.save")}
        </Button>
      </div>

      {msg && (
        <div className="mt-3 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-[13px] text-green-700">
          {t("autoSignOut.updated")}
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700">
          {errorText}
        </div>
      )}
    </>
  );

  if (mode === "inlay") {
    return (
      <div
        ref={rootRef}
        className="fixed inset-0 z-[1000] bg-black/40 overflow-y-auto"
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        {/* top-align so dropdown has space */}
        <div className="min-h-full flex items-start justify-center p-4 pt-16 pb-10">
          {/* ✅ IMPORTANT: do NOT use overflow-hidden (it clips AppleComboBox dropdown) */}
          <div className={`w-full max-w-lg ${A.modal} overflow-visible`}>
            {/* centered title header */}
            <div className="grid grid-cols-[40px_1fr_40px] items-center px-5 py-4 border-b border-gray-100">
              <div />
              <h3 className={A.title + " text-center"}>{t("accountMenu.autoSignOut")}</h3>
              <button
                onClick={onClose}
                className={[
                  A.iconBtn,
                  "justify-self-end inline-flex items-center justify-center",
                  "hover:bg-gray-100",
                  "focus:outline-none focus:ring-2 focus:ring-appleblueD",
                ].join(" ")}
                aria-label={t("directory.common.close")}
                type="button"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
              </button>
            </div>

            <div className={`px-6 py-6 ${A.body}`}>{content}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-2 py-6`}>
      <div className={`w-full max-w-md ${A.card} p-6`}>
        <div className="text-center mb-4">
          <h2 className={A.title}>{t("accountMenu.autoSignOut")}</h2>
        </div>
        {content}
      </div>
    </div>
  );
}
